import { supabase } from "@/integrations/supabase/client";

export interface StripeSoloInvoiceRow {
  id: string;
  stripe_session_id: string | null;
  stripe_customer_email: string | null;
  stripe_currency: string | null;
  stripe_amount_total: number | null;
  stripe_discount_total: number | null;
  solo_invoice_number: string | null;
  solo_pdf_url: string | null;
  status: string;
  error_code: string | null;
  error_message: string | null;
  invoice_email_to: string | null;
  invoice_email_sent_at: string | null;
  invoice_email_error: string | null;
  solo_request_payload: unknown;
  solo_response_payload: unknown;
  processed_at: string | null;
  created_at: string;
}

export interface EmailSendAttemptRow {
  id: string;
  source_function: string;
  email_type: string;
  provider: string;
  recipient_role: string | null;
  recipient_email: string | null;
  status: string;
  provider_message_id: string | null;
  error_message: string | null;
  related_order_id: string | null;
  stripe_event_id: string | null;
  stripe_session_id: string | null;
  payload: unknown;
  attempted_at: string;
  completed_at: string | null;
  created_at: string;
}

export interface StripeWebhookLogRow {
  id: string;
  request_id: string;
  stripe_event_id: string | null;
  stripe_event_type: string | null;
  stripe_session_id: string | null;
  stripe_payment_intent_id: string | null;
  level: string;
  status: string;
  http_status: number | null;
  error_message: string | null;
  error_stack: string | null;
  tags: string[];
  flow: unknown;
  request_method: string | null;
  request_url: string | null;
  request_headers: unknown;
  response_body: unknown;
  metadata: unknown;
  order_id: string | null;
  order_contact_email: string | null;
  order_contact_name: string | null;
  order_event_title: string | null;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  created_at: string;
}

export interface StripeWebhookLogQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
}

export interface StripeSoloInvoiceQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
}

export interface StripeSoloInvoiceResult {
  rows: StripeSoloInvoiceRow[];
  count: number;
}

export interface StripeWebhookLogResult {
  rows: StripeWebhookLogRow[];
  count: number;
}

export interface AdminDashboardService {
  getUnreadOrderCount(): Promise<number>;
  listStripeSoloInvoices(query?: StripeSoloInvoiceQuery): Promise<StripeSoloInvoiceResult>;
  listEmailSendAttempts(limit?: number): Promise<EmailSendAttemptRow[]>;
  listStripeWebhookLogs(query?: StripeWebhookLogQuery): Promise<StripeWebhookLogResult>;
  deleteStripeWebhookLogs(ids: string[]): Promise<void>;
  invokeMockSoloInvoice(rawPayload: string): Promise<unknown>;
  sendTestEmail(input: { toEmail: string; subject: string; message: string; locale?: "hr" | "en" }): Promise<unknown>;
  sendInvoiceEmail(input: { invoiceId: string; toEmail?: string; message?: string; force?: boolean; locale?: "hr" | "en" }): Promise<unknown>;
  sendEventAccessEmail(input: { eventId: string; toEmail: string; locale?: "hr" | "en" }): Promise<unknown>;
  sendPhotoExpiryEmail(input: { eventId: string; toEmail: string; locale?: "hr" | "en" }): Promise<unknown>;
  sendStorageLimitEmail(input: { eventId: string; toEmail: string; locale?: "hr" | "en" }): Promise<unknown>;
}

export class AdminFunctionError extends Error {
  functionName: string;
  status: number | null;
  body: unknown;

  constructor(functionName: string, message: string, input: { status?: number | null; body?: unknown } = {}) {
    super(message);
    this.name = "AdminFunctionError";
    this.functionName = functionName;
    this.status = input.status ?? null;
    this.body = input.body ?? null;
  }
}

const readFunctionErrorBody = async (error: any): Promise<{ status: number | null; body: unknown; message: string }> => {
  const response = error?.context;
  if (response && typeof response.clone === "function") {
    const cloned = response.clone();
    const status = typeof cloned.status === "number" ? cloned.status : null;
    const contentType = cloned.headers?.get?.("content-type") ?? "";
    try {
      const body = contentType.includes("application/json") ? await cloned.json() : await cloned.text();
      const message = typeof body === "object" && body && "message" in body
        ? String((body as { message?: unknown }).message)
        : error?.message || "Function returned a non-2xx status code";
      return { status, body, message };
    } catch {
      return { status, body: null, message: error?.message || "Function returned a non-2xx status code" };
    }
  }

  return { status: null, body: null, message: error?.message || "Function returned a non-2xx status code" };
};

const invokeFunction = async <T>(functionName: string, body: unknown, options?: { headers?: Record<string, string> }): Promise<T> => {
  const { data, error } = await supabase.functions.invoke(functionName, {
    body,
    headers: options?.headers,
  });

  if (error) {
    const details = await readFunctionErrorBody(error);
    throw new AdminFunctionError(functionName, details.message, {
      status: details.status,
      body: details.body,
    });
  }

  return data as T;
};

const isMissingStripeSoloInvoicesTable = (error: any): boolean => {
  const message = String(error?.message ?? "");
  return error?.code === "PGRST205"
    || error?.code === "42P01"
    || message.includes("public.stripe_solo_invoices")
    || message.includes("stripe_solo_invoices");
};

const stripeSoloInvoicesMissingError = () =>
  new Error("PostgREST schema cache cannot see public.stripe_solo_invoices. If the table exists, reload the Supabase schema cache (for SQL: NOTIFY pgrst, 'reload schema';) and confirm VITE_SUPABASE_URL points at the project where the table exists.");

const isMissingEmailSendAttemptsTable = (error: any): boolean => {
  const message = String(error?.message ?? "");
  return error?.code === "PGRST205"
    || error?.code === "42P01"
    || message.includes("public.email_send_attempts")
    || message.includes("email_send_attempts");
};

const emailSendAttemptsMissingError = () =>
  new Error("PostgREST schema cache cannot see public.email_send_attempts. If the table exists, reload the Supabase schema cache (for SQL: NOTIFY pgrst, 'reload schema';) and confirm VITE_SUPABASE_URL points at the project where the table exists.");

const isMissingStripeWebhookLogsTable = (error: any): boolean => {
  const message = String(error?.message ?? "");
  return error?.code === "PGRST205"
    || error?.code === "42P01"
    || message.includes("public.stripe_webhook_logs")
    || message.includes("stripe_webhook_logs");
};

const stripeWebhookLogsMissingError = () =>
  new Error("PostgREST schema cache cannot see public.stripe_webhook_logs. If the table exists, reload the Supabase schema cache (for SQL: NOTIFY pgrst, 'reload schema';) and confirm VITE_SUPABASE_URL points at the project where the table exists.");

const sanitizePostgrestSearch = (value: string): string =>
  value.trim().replace(/[%(),]/g, " ").replace(/\s+/g, " ").slice(0, 120);

export const supabaseAdminDashboardService: AdminDashboardService = {
  async getUnreadOrderCount() {
    const { count, error } = await supabase
      .from("orders")
      .select("id", { count: "exact", head: true })
      .eq("is_read", false);

    if (error) throw error;
    return count ?? 0;
  },

  async listStripeSoloInvoices(query = {}) {
    const page = Math.max(1, query.page ?? 1);
    const pageSize = Math.min(100, Math.max(10, query.pageSize ?? 25));
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    const search = sanitizePostgrestSearch(query.search ?? "");

    let request = supabase
      .from("stripe_solo_invoices")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    if (query.status && query.status !== "all") {
      request = request.eq("status", query.status);
    }

    if (search) {
      const pattern = `*${search}*`;
      request = request.or([
        `solo_invoice_number.ilike.${pattern}`,
        `stripe_customer_email.ilike.${pattern}`,
        `invoice_email_to.ilike.${pattern}`,
        `stripe_session_id.ilike.${pattern}`,
        `error_message.ilike.${pattern}`,
        `status.ilike.${pattern}`,
      ].join(","));
    }

    const { data, error, count } = await request;

    if (error) {
      if (isMissingStripeSoloInvoicesTable(error)) throw stripeSoloInvoicesMissingError();
      throw error;
    }
    return { rows: (data ?? []) as StripeSoloInvoiceRow[], count: count ?? 0 };
  },

  async listEmailSendAttempts(limit = 100) {
    const { data, error } = await supabase
      .from("email_send_attempts")
      .select("*")
      .order("attempted_at", { ascending: false })
      .limit(limit);

    if (error) {
      if (isMissingEmailSendAttemptsTable(error)) throw emailSendAttemptsMissingError();
      throw error;
    }
    return (data ?? []) as EmailSendAttemptRow[];
  },

  async listStripeWebhookLogs(query = {}) {
    const page = Math.max(1, query.page ?? 1);
    const pageSize = Math.min(100, Math.max(10, query.pageSize ?? 25));
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    const search = sanitizePostgrestSearch(query.search ?? "");

    let request = supabase
      .from("stripe_webhook_logs")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    if (query.status && query.status !== "all") {
      request = request.eq("status", query.status);
    }

    if (search) {
      const pattern = `*${search}*`;
      request = request.or([
        `request_id.ilike.${pattern}`,
        `stripe_event_id.ilike.${pattern}`,
        `stripe_event_type.ilike.${pattern}`,
        `stripe_session_id.ilike.${pattern}`,
        `order_contact_email.ilike.${pattern}`,
        `order_contact_name.ilike.${pattern}`,
        `order_event_title.ilike.${pattern}`,
        `error_message.ilike.${pattern}`,
      ].join(","));
    }

    const { data, error, count } = await request;

    if (error) {
      if (isMissingStripeWebhookLogsTable(error)) throw stripeWebhookLogsMissingError();
      throw error;
    }
    return { rows: (data ?? []) as StripeWebhookLogRow[], count: count ?? 0 };
  },

  async deleteStripeWebhookLogs(ids) {
    const uniqueIds = Array.from(new Set(ids)).filter(Boolean);
    if (uniqueIds.length === 0) return;

    const { error } = await supabase
      .from("stripe_webhook_logs")
      .delete()
      .in("id", uniqueIds);

    if (error) {
      if (isMissingStripeWebhookLogsTable(error)) throw stripeWebhookLogsMissingError();
      throw error;
    }
  },

  async invokeMockSoloInvoice(rawPayload) {
    return invokeFunction("mock-solo-invoice", rawPayload, {
      headers: { "Content-Type": "application/x-www-form-urlencoded", "x-persist-mock-invoice": "true" },
    });
  },

  async sendTestEmail(input) {
    return invokeFunction("send-invoice-email", {
        mode: "test",
        to_email: input.toEmail,
        subject: input.subject,
        message: input.message,
        locale: input.locale,
        source: "superadmin-email-test",
    });
  },

  async sendInvoiceEmail(input) {
    return invokeFunction("send-invoice-email", {
        mode: "invoice",
        invoice_id: input.invoiceId,
        to_email: input.toEmail || undefined,
        message: input.message || undefined,
        force: !!input.force,
        locale: input.locale,
        source: "superadmin-invoice-email",
    });
  },

  async sendEventAccessEmail(input) {
    return invokeFunction("send-invoice-email", {
        mode: "event_access",
        event_id: input.eventId,
        to_email: input.toEmail,
        locale: input.locale,
        source: "superadmin-event-access",
    });
  },

  async sendPhotoExpiryEmail(input) {
    return invokeFunction("send-invoice-email", {
        mode: "photo_expiry",
        event_id: input.eventId,
        to_email: input.toEmail,
        locale: input.locale,
        source: "superadmin-photo-expiry",
    });
  },

  async sendStorageLimitEmail(input) {
    return invokeFunction("send-invoice-email", {
        mode: "storage_limit",
        event_id: input.eventId,
        to_email: input.toEmail,
        locale: input.locale,
        source: "superadmin-storage-limit",
    });
  },
};
